import { AuthService } from '@app/core/services/auth.service';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { Reminder, ReminderFormData, ReminderHostType } from '@app/core/models/reminder.model';
import { SubSink } from 'subsink';
import { ContextService } from '@app/core/services/context.service';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ReminderService implements OnDestroy {
  private apiUrl = `${environment.apiUrl}/reminders`;
  private subsink = new SubSink();

  constructor(private http: HttpClient,
              private contextService: ContextService,
              private authService: AuthService) {
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }


  /**
   * Get all reminders
   */
  getReminders(isActive: boolean | undefined, page: number, pageSize: number): Observable<ApiResponse<Reminder[]>> {
    const userId = this.authService.currentUserValue?.id;
    let url = `${this.apiUrl}/${this.contextService.getCurrentClientId()}/${this.contextService.getCurrentSubsidiaryId()}/${userId}`;
    url += `?page=${page}&pageSize=${pageSize}`;
    if (isActive !== undefined)  url += `&isActive=${isActive}`;

    return this.http.get<ApiResponse<Reminder[]>>(url);
  }

  /**
   * Get all remonders for a specific host (claim, policy, ...)
   */
  getRemindersByHost(hostType: ReminderHostType, hostId: string): Observable<ApiResponse<Reminder[]>> {
    const userId = this.authService.currentUserValue?.id;
    const url = `${this.apiUrl}/${this.contextService.getCurrentClientId()}/${this.contextService.getCurrentSubsidiaryId()}/${userId}?hostType=${hostType}&hostId=${hostId}`;
    return this.http.get<ApiResponse<Reminder[]>>(url);
  }

  /**
   * Get a single reminder by ID
   */
  getReminder(id: string): Observable<ApiResponse<Reminder>> {
    const userId = this.authService.currentUserValue?.id;
    const url = `${this.apiUrl}/${this.contextService.getCurrentClientId()}/${this.contextService.getCurrentSubsidiaryId()}/${userId}/${id}`;
    return this.http.get<ApiResponse<Reminder>>(url);
  }

  getDueRemindersCount(userId: string): Observable<ApiResponse<{dueCount: number}>> {
    const url = `${this.apiUrl}/${userId}/due-count`;
    return this.http.get<ApiResponse<{dueCount: number}>>(url);
  }

  /**
   * Create a new reminder
   */
  createReminder(formData: ReminderFormData): Observable<ApiResponse<Reminder>> {
    const userId = this.authService.currentUserValue?.id;
    const url = `${this.apiUrl}/${this.contextService.getCurrentClientId()}/${this.contextService.getCurrentSubsidiaryId()}/${userId}`;
    return this.http.post<ApiResponse<Reminder>>(url, formData);
  }

  /**
   * Update an existing reminder
   */
  updateReminder(id: string, formData: ReminderFormData): Observable<ApiResponse<Reminder>> {
    const userId = this.authService.currentUserValue?.id;
    const url = `${this.apiUrl}/${this.contextService.getCurrentClientId()}/${this.contextService.getCurrentSubsidiaryId()}/${userId}/${id}`;
    return this.http.put<ApiResponse<Reminder>>(url, formData);
  }

  /**
   * Delete a reminder (soft delete)
   */
  deleteReminder(id: string): Observable<ApiResponse<void>> {
    const userId = this.authService.currentUserValue?.id;
    const url = `${this.apiUrl}/${this.contextService.getCurrentClientId()}/${this.contextService.getCurrentSubsidiaryId()}/${userId}/${id}`;
    return this.http.delete<ApiResponse<void>>(url);
  }
}
